import React, { useCallback, useState, useContext, createContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useInterval } from "../../hooks";
import useGatsbyAuth from "../../templates/user-account/hook/useGatsbyAuth";
import { getAccountCreatedStatus } from "../../utils/Account/helper";

const UserContext = createContext({
	user: null,
	isLoggedIn: false,
	affiliate: {
		code: null,
		name: null,
	},
	isRegistrationRedirect: false,
	profileTab: null, // my-trips | profile
	signOut: () => {},
});

const useUser = () => useContext(UserContext);

// eslint-disable-next-line no-unused-vars
const UserProvider = ({ children, lang }) => {
	const { loading: authServiceLoading, authState, authService } = useGatsbyAuth();
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState();
	const [affiliate, setAffiliate] = useState({
		code: "wvi",
		name: null,
	});
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [isRegistrationRedirect, setIsRegistrationRedirect] = useState(true);
	const [profileTab, setProfileTab] = useState();

	const updateUser = _user => {
		setLoading(false);
		setUser(_user);

		if (!authServiceLoading) {
			if (_user && authState && authState.isAuthenticated) {
				setIsLoggedIn(true);
			} else {
				setIsLoggedIn(false);
				authService.tokenManager.clear();
			}
		}
	};

	const refreshSession = useCallback(async () => {
		if (document.visibilityState === "visible") {
			await authService.session
				.get()
				.then(session => {
					// If the session is valid & active, get the user data and update the user
					if (session.status === "ACTIVE") {
						session.user().then(async sessionUser => {
							// Flag for Agent
							// const isAgent = !!authState?.idToken?.claims?.agentGlobalId;
							updateUser(sessionUser);
						});
					} else if (document.visibilityState === "visible") {
						updateUser();
					}
				})
				.catch(() => {
					// If no session, clear the user.
					if (document.visibilityState === "visible") {
						updateUser();
					}
				});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState]);

	useEffect(() => {
		// On load
		if (!authServiceLoading && authState && !getAccountCreatedStatus()) {
			(async () => {
				await refreshSession();
			})();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authServiceLoading, authState]);

	useEffect(() => {
		// Check the session on window focus
		window.addEventListener("visibilitychange", refreshSession);

		return () => {
			window.removeEventListener("visibilitychange", refreshSession);
		};
	}, [refreshSession]);

	// Check the session and update the authState every 5 minutes
	useInterval(refreshSession, 5 * 60 * 1000);

	const value = {
		loading,
		isLoggedIn,
		setIsLoggedIn,
		user,
		updateUser,
		authState,
		authService,
		affiliate,
		setAffiliate,
		isRegistrationRedirect,
		setIsRegistrationRedirect,
		refreshSession,
		profileTab,
		setProfileTab,
		signOut: redirectUrl => {
			// Signs out of Okta session, removes localStorage & invalidates SID cookie
			authService.signOut({
				postLogoutRedirectUri: redirectUrl,
			});
		},
	};

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
	lang: PropTypes.string.isRequired,
};

export { UserProvider, useUser };
